import {
    Button,
    Modal,
    PropagationNotice,
    TextAreaField,
    TextField,
    Toggle,
    WriteError,
} from '@shared/ui';
import { useState } from 'react';

import { useSetOverride } from '../hooks/use-flags';

import { overrideConfirmCopy } from './override-confirm-copy';
import { offersRollout } from './rollout-ui';

import type { FlagKind } from '../schemas/flag-type';

/**
 * Override one museum's value for this flag.
 *
 * ## Why the museum is an id typed by hand
 *
 * Because there is nothing to pick from yet. Overrides carry a bare `tenantId`,
 * and rendering "The Broad" instead of `00Dxx0000001aBcD` needs a tenants
 * service this app does not talk to — it is listed as open in the module README.
 * A fake picker over invented names would be worse than an honest text field:
 * the operator would trust the label and have no way to check it.
 *
 * ## Why there is a confirm step
 *
 * A mistyped id is not destructive on its own — the row addresses a museum that
 * does not exist and can be reverted. The confirm step is there for the other
 * half of the decision (ADR 0009 §5, NOXSCRUM-1670): it names the org and says
 * what kind of flag this is. For a `salesforce` flag the value leaves the
 * platform and lands in that customer's org; for a `veevart2` flag it never
 * does. See `override-confirm-copy.ts`.
 *
 * A `salesforce` flag offers no rollout field: the org side is a checkbox, and
 * the API refuses a percentage on it with `ROLLOUT_NOT_ALLOWED`.
 */
export function OverrideDialog({
    flagKey,
    kind,
    open,
    onClose,
}: {
    flagKey: string;
    kind: FlagKind;
    open: boolean;
    onClose: () => void;
}) {
    const [tenantId, setTenantId] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [rollout, setRollout] = useState('');
    const [reason, setReason] = useState('');
    const [confirming, setConfirming] = useState(false);
    const setOverride = useSetOverride(flagKey);

    const isSalesforce = kind.type === 'salesforce';

    const close = () => {
        setTenantId('');
        setEnabled(true);
        setRollout('');
        setReason('');
        setConfirming(false);
        setOverride.reset();
        onClose();
    };

    // Nothing typed can reach the write while the field is hidden (`rollout-ui.ts`).
    const trimmedRollout = offersRollout(kind.type) ? rollout.trim() : '';
    const rolloutValue = trimmedRollout.length === 0 ? undefined : Number(trimmedRollout);
    const rolloutError =
        rolloutValue !== undefined &&
        (!Number.isInteger(rolloutValue) || rolloutValue < 0 || rolloutValue > 100)
            ? 'Enter a whole number from 0 to 100, or leave it blank for no gate.'
            : undefined;

    const complete = tenantId.trim().length > 0 && rolloutError === undefined;

    const copy = overrideConfirmCopy(flagKey, kind, {
        tenantId: tenantId.trim(),
        enabled,
        rolloutPercentage: rolloutValue,
    });

    const save = () => {
        setOverride.mutate(
            {
                tenantId: tenantId.trim(),
                enabled,
                ...(rolloutValue !== undefined ? { rolloutPercentage: rolloutValue } : {}),
                ...(reason.trim().length > 0 ? { reason: reason.trim() } : {}),
            },
            { onSuccess: close },
        );
    };

    return (
        <Modal
            open={open}
            onClose={close}
            dismissable={!setOverride.isPending}
            title={confirming ? copy.title : isSalesforce ? 'Override an org' : 'Override a museum'}
            description={
                confirming
                    ? copy.description
                    : `Replaces the platform default of ${flagKey} for one ${isSalesforce ? 'org' : 'museum'}. Overrides are not versioned — the last write wins.`
            }
            footer={
                confirming ? (
                    <>
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setConfirming(false);
                            }}
                            disabled={setOverride.isPending}
                        >
                            Back
                        </Button>
                        <Button disabled={setOverride.isPending} onClick={save}>
                            {setOverride.isPending ? 'Saving…' : 'Save override'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="ghost" onClick={close}>
                            Cancel
                        </Button>
                        <Button
                            disabled={!complete}
                            onClick={() => {
                                setConfirming(true);
                            }}
                        >
                            Review
                        </Button>
                    </>
                )
            }
        >
            {confirming ? (
                <div className="flex flex-col gap-4">
                    <div className="rounded-lg border border-accent-line bg-accent-surface px-4 py-3.5">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-accent-text">
                            {isSalesforce ? 'Salesforce org' : 'Museum'}
                        </div>
                        <div className="mt-1.5 break-all font-mono text-[15px] font-semibold text-content">
                            {tenantId.trim()}
                        </div>
                        <div className="mt-1 text-[12.5px] text-content-body">
                            {enabled ? 'On' : 'Off'}
                            {rolloutValue !== undefined && `, gated at ${String(rolloutValue)}%`}
                            {isSalesforce && kind.salesforceField !== undefined && (
                                <>
                                    {' '}
                                    → <span className="font-mono">{kind.salesforceField}</span>
                                </>
                            )}
                        </div>
                    </div>

                    {setOverride.error && <WriteError error={setOverride.error} />}

                    <PropagationNotice />
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <TextField
                        label="Museum (tenant id)"
                        value={tenantId}
                        onChange={setTenantId}
                        placeholder="00Dxx0000001aBcD"
                        mono
                        autoFocus
                        hint="The Salesforce org id. Names are not resolvable here yet — the Backoffice does not read the tenants service."
                    />

                    <div className="flex flex-col gap-1.5">
                        <span className="text-[12.5px] font-semibold text-content-body">Value</span>
                        <div className="flex items-center gap-3">
                            <Toggle
                                on={enabled}
                                onChange={setEnabled}
                                showLabel
                                aria-label="Override value"
                            />
                            <span className="text-[12.5px] text-content-muted">
                                What this museum gets instead of the platform default.
                            </span>
                        </div>
                    </div>

                    {isSalesforce ? (
                        <p className="text-[12px] leading-relaxed text-content-muted">
                            No rollout percentage: a Salesforce flag is a checkbox per org.
                        </p>
                    ) : (
                        offersRollout(kind.type) && (
                            <TextField
                                label="Rollout % (optional)"
                                value={rollout}
                                onChange={setRollout}
                                placeholder="Leave blank for no gate"
                                error={rolloutError}
                                hint="0 means off for everyone — it is not the same as blank."
                            />
                        )
                    )}

                    <TextAreaField
                        label="Reason (optional)"
                        value={reason}
                        onChange={setReason}
                        rows={2}
                        placeholder="Why this museum differs"
                        hint="Recorded on the audit entry."
                    />

                    <PropagationNotice />
                </div>
            )}
        </Modal>
    );
}
